import React, { useState, useEffect } from 'react';
import { CognitionResponses, DomainResult } from '../../types/index';
import { locationService } from '../../services/locationService';

interface CognitionAssessmentProps {
  onComplete: (result: DomainResult) => void;
  onNext: () => void;
  readOnlyMode?: boolean;
  initialData?: DomainResult;
}

const CognitionAssessment: React.FC<CognitionAssessmentProps> = ({ onComplete, onNext, readOnlyMode = false, initialData }) => {
  const [currentStep, setCurrentStep] = useState<'filter' | 'word_instruction' | 'time_year' | 'time_month' | 'time_date' | 'time_weekday' | 'space_city' | 'space_place' | 'space_area' | 'space_transport' | 'word_recall' | 'results' | 'carer_correction' | 'completed'>('filter');
  const [responses, setResponses] = useState<CognitionResponses>({});
  const [wordRecallWords] = useState(['花', '门', '米饭']);
  
  // 时间定向测试状态
  const [timeResults, setTimeResults] = useState({
    year: null as boolean | null,
    month: null as boolean | null, 
    date: null as boolean | null,
    weekday: null as boolean | null
  });
  
  
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  
  // 用户的具体答案存储
  const [userAnswers, setUserAnswers] = useState({
    year: '',
    month: '',
    date: '', 
    weekday: '',
    city: '',
    place: '',
    area: '',
    transport: ''
  });

  const [suggestedCity, setSuggestedCity] = useState<string | null>(null);
  const [locationFetched, setLocationFetched] = useState(false);
  
  // 照护者修正结果
  const [carerCorrection, setCarerCorrection] = useState({
    timeCorrect: null as boolean | null,
    spaceCorrect: null as boolean | null
  });
  

  // 获取当前时间信息的函数
  const getCurrentTimeInfo = () => {
    const now = new Date();
    const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      date: now.getDate(),
      weekdayName: weekdays[now.getDay()]
    };
  };

  useEffect(() => {
    if (currentStep === 'space_city' && !locationFetched) {
      setLocationFetched(true);
      locationService
        .getApproximateCity()
        .then((location) => {
          if (location?.city) {
            setSuggestedCity(location.city);
          }
        })
        .catch(() => {
          /* 静默失败 */
        });
    }
  }, [currentStep, locationFetched]);

  // 评估逻辑实现
  const evaluateCognition = (responses: CognitionResponses): DomainResult => {
    // 第一步：检查Filter Question
    if (responses.filterQuestion === true) {
      // 回答"是的，我有这些问题" → 直接FAIL
      return {
        status: 'FAIL',
        completionMethod: 'filter_question',
        filterQuestionResult: false, // 修正：筛查问题回答"是"表示筛查失败
        testResults: [
          {
            name: "认知问题筛查",
            passed: false,
            details: "筛查问题：存在记忆或定向问题"
          }
        ],
        recommendations: [
          "ICOPE-C3-S3.1.5-Community-Care-Overview",
          "ICOPE-C5-S5.2-Community-Care-Overview"
        ],
        // 🎯 双数据源支持：筛查问题失败时也添加responses字段
        responses: {
          filterQuestion: true
        }
      };
    }
    
    // 第二步：Filter Question回答"否" → 进行详细测试
    // 基于新的步骤式评估进行结果计算
    const timeCorrectCount = Object.values(timeResults).filter(result => result === true).length;
    const timeCorrect = timeCorrectCount === 4; // 时间定向：4题全对才算通过
    
    // 空间定向：系统自动验证逻辑一致性
    const spaceValidation = validateSpaceAnswers();
    const spaceCorrect = spaceValidation.passed;
    const wordsCorrect = responses.wordRecall?.correctCount === 3;
    
    const allTestsPassed = timeCorrect && spaceCorrect && wordsCorrect;
    
    return {
      status: allTestsPassed ? 'PASS' : 'FAIL',
      completionMethod: 'full_test',
      filterQuestionResult: true, // 修正：筛查问题回答"否"表示筛查通过
      testResults: [
        { name: "认知问题筛查", passed: true, details: "筛查问题：无记忆或定向问题" },
        { 
          name: "时间定向", 
          passed: timeCorrect, 
          details: `${timeCorrectCount}/4 (需全对)`,
          rawUserAnswers: {
            answers: {
              year: userAnswers.year,
              month: userAnswers.month,
              date: userAnswers.date,
              weekday: userAnswers.weekday
            },
            results: {
              year: timeResults.year,
              month: timeResults.month,
              date: timeResults.date,
              weekday: timeResults.weekday
            }
          }
        },
        { 
          name: "空间定向", 
          passed: spaceCorrect, 
          details: spaceValidation.details.logicalError ? `逻辑冲突: ${spaceValidation.details.errorReason}` : '逻辑合理',
          rawUserAnswers: {
            answers: {
              city: userAnswers.city,
              place: userAnswers.place,
              area: userAnswers.area,
              transport: userAnswers.transport
            },
            validation: {
              passed: spaceValidation.passed,
              details: spaceValidation.details
            }
          }
        },
        { 
          name: "三词记忆", 
          passed: wordsCorrect, 
          details: `${responses.wordRecall?.correctCount || 0}/3`,
          rawUserAnswers: {
            answers: {
              selectedWords: responses.wordRecall?.selectedWords || [],
              correctCount: responses.wordRecall?.correctCount || 0,
              totalWords: responses.wordRecall?.totalWords || 3
            }
          }
        }
      ],
      recommendations: allTestsPassed ? 
        ["ICOPE-C3-S3.1.3-Advice-BrainHealth"] : 
        ["ICOPE-C3-S3.1.5-Community-Care-Overview"],
      // 🎯 双数据源支持：添加responses字段用于评估详情页面显示
      responses: {
        wordRecall: responses.wordRecall,
        cognitiveTests: {
          timeOrientation: {
            userInputs: userAnswers
          },
          spaceOrientation: {
            userInputs: userAnswers
          }
        }
      }
    };
  };

  const handleFilterQuestion = (hasProblems: boolean) => {
    const updatedResponses = { ...responses, filterQuestion: hasProblems };
    setResponses(updatedResponses);
    
    if (hasProblems) {
      // 直接完成评估
      const result = evaluateCognition(updatedResponses);
      onComplete(result);
      setCurrentStep('completed');
    } else {
      // 继续详细测试
      setCurrentStep('word_instruction');
    }
  };

  const handleWordInstructionNext = () => {
    setCurrentStep('time_year');
  };

  // 时间定向测试处理函数
  const handleTimeYear = (answer: string) => {
    const currentYear = getCurrentTimeInfo().year.toString();
    const isCorrect = answer === currentYear;
    setTimeResults(prev => ({ ...prev, year: isCorrect }));
    setUserAnswers(prev => ({ ...prev, year: answer }));
    setCurrentStep('time_month');
  };

  const handleTimeMonth = (answer: string) => {
    const currentMonth = getCurrentTimeInfo().month.toString();
    const isCorrect = answer === currentMonth;
    setTimeResults(prev => ({ ...prev, month: isCorrect }));
    setUserAnswers(prev => ({ ...prev, month: answer }));
    setCurrentStep('time_date');
  };

  const handleTimeDate = (answer: string) => {
    const currentDate = getCurrentTimeInfo().date.toString();
    const isCorrect = answer === currentDate;
    setTimeResults(prev => ({ ...prev, date: isCorrect }));
    setUserAnswers(prev => ({ ...prev, date: answer }));
    setCurrentStep('time_weekday');
  };

  const handleTimeWeekday = (answer: string) => {
    const currentWeekday = getCurrentTimeInfo().weekdayName;
    const isCorrect = answer === currentWeekday;
    setTimeResults(prev => ({ ...prev, weekday: isCorrect }));
    setUserAnswers(prev => ({ ...prev, weekday: answer }));
    setCurrentStep('space_city');
  };

  // 空间定向测试处理函数
  const handleSpaceCity = (answer: string) => {
    setUserAnswers(prev => ({ ...prev, city: answer }));
    setCurrentStep('space_place');
  };

  // 空间定向测试验证逻辑
  const validateSpaceAnswers = () => {
    const { city, place, area, transport } = userAnswers;
    
    // 基础验证：所有选项都必须有效
    const cityValid = true; // 城市答案仅用于记录，不计入对错
    const placeValid = ['在家里', '在医院', '在诊所', '在社区中心', '在其他地方'].includes(place);
    const areaValid = ['客厅', '卧室', '书房', '诊室', '等候区', '其他房间'].includes(area);
    const transportValid = ['步行来的', '开车来的', '家人送来的', '乘坐公交来的', '其他方式'].includes(transport);
    
    // 逻辑冲突检查：检查明显不合理的组合
    let hasLogicalError = false;
    let errorReason = '';
    
    // 医疗场所 + 家居区域 = 逻辑错误
    if ((place === '在医院' || place === '在诊所') && ['客厅', '卧室', '书房'].includes(area)) {
      hasLogicalError = true;
      errorReason = '医疗场所不应该有家居区域';
    }
    
    // 家里 + 医疗区域 = 逻辑错误  
    if (place === '在家里' && ['诊室', '等候区'].includes(area)) {
      hasLogicalError = true;
      errorReason = '家里不应该有医疗区域';
    }
    
    // 在家里但说是开车/公交来的 = 逻辑错误（步行和其他方式除外）
    if (place === '在家里' && ['开车来的', '乘坐公交来的', '家人送来的'].includes(transport)) {
      hasLogicalError = true;
      errorReason = '在家里不需要交通工具到达';
    }
    
    // 所有基础答案有效 且 无逻辑冲突 = 通过
    const allBasicValid = cityValid && placeValid && areaValid && transportValid;
    const passed = allBasicValid && !hasLogicalError;
    
    return {
      passed,
      details: {
        city: cityValid,
        place: placeValid, 
        area: areaValid,
        transport: transportValid,
        logicalError: hasLogicalError,
        errorReason,
        allBasicValid
      }
    };
  };

  const handleSpacePlace = (answer: string) => {
    setUserAnswers(prev => ({ ...prev, place: answer }));
    setCurrentStep('space_area');
  };

  const handleSpaceArea = (answer: string) => {
    setUserAnswers(prev => ({ ...prev, area: answer }));
    setCurrentStep('space_transport');
  };

  const handleSpaceTransport = (answer: string) => {
    setUserAnswers(prev => ({ ...prev, transport: answer }));
    // 空间定向测试完成，直接进入词汇记忆测试
    setCurrentStep('word_recall');
  };


  const handleWordRecall = (selectedWords: string[]) => {
    const correctCount = selectedWords.filter(word => wordRecallWords.includes(word)).length;
    const updatedResponses = {
      ...responses,
      wordRecall: { selectedWords, correctCount, totalWords: 3 }
    };
    
    setResponses(updatedResponses);
    setCurrentStep('results');
  };

  // 处理测试结果确认
  const handleResultsConfirm = () => {
    const result = evaluateCognition(responses);
    onComplete(result);
    setCurrentStep('completed');
  };

  // 处理照护者修正
  const handleCarerCorrection = (timeCorrect: boolean, spaceCorrect: boolean) => {
    setCarerCorrection({ timeCorrect, spaceCorrect });
    
    // 基于照护者修正重新评估
    const wordsCorrectCount = responses.wordRecall?.correctCount || 0;
    const allTestsPassed = timeCorrect && spaceCorrect && (wordsCorrectCount === 3);
    
    const result: DomainResult = {
      status: allTestsPassed ? 'PASS' : 'FAIL',
      completionMethod: 'full_test',
      filterQuestionResult: false,
      testResults: [
        { 
          name: "时间定向", 
          passed: timeCorrect, 
          details: "照护者确认",
          rawUserAnswers: {
            answers: {
              year: userAnswers.year,
              month: userAnswers.month,
              date: userAnswers.date,
              weekday: userAnswers.weekday
            },
            results: {
              year: timeCorrect, // 照护者确认的结果
              month: timeCorrect,
              date: timeCorrect,
              weekday: timeCorrect
            },
            carerCorrected: true
          }
        },
        { 
          name: "空间定向", 
          passed: spaceCorrect, 
          details: "照护者确认",
          rawUserAnswers: {
            answers: {
              city: userAnswers.city,
              place: userAnswers.place,
              area: userAnswers.area,
              transport: userAnswers.transport
            },
            validation: {
              passed: spaceCorrect,
              details: {
                city: true,
                place: true,
                area: true,
                transport: true,
                logicalError: false,
                errorReason: '',
                allBasicValid: true
              }
            },
            carerCorrected: true
          }
        },
        { 
          name: "三词记忆", 
          passed: wordsCorrectCount === 3, 
          details: `${wordsCorrectCount}/3`,
          rawUserAnswers: {
            answers: {
              selectedWords: responses.wordRecall?.selectedWords || [],
              correctCount: wordsCorrectCount,
              totalWords: 3
            }
          }
        }
      ],
      recommendations: allTestsPassed ? 
        ["ICOPE-C3-S3.1.3-Advice-BrainHealth"] : 
        [
          "ICOPE-C3-S3.1.5-Community-Care-Overview",
          "ICOPE-C5-S5.2-Community-Care-Overview"
        ]
    };
    
    onComplete(result);
    setCurrentStep('completed');
  };


  useEffect(() => {
    if (currentStep === 'completed') {
      const timer = setTimeout(() => {
        onNext();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  const renderFilterQuestion = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">认知能力评估</h2>
        <p className="text-gray-600">让我们开始认知能力的评估</p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3">请回答以下问题：</h3>
        <p className="text-lg text-gray-800 mb-4">
          您有记忆或定向方面的问题吗？<br />
          <span className="text-sm text-gray-600">
            （定向是指对时间、地点、人物等的判断能力，比如：不清楚今天是几号、不知道自己在什么地方）
          </span>
        </p>
        
        <div className="space-y-3">
          <button
            onClick={() => handleFilterQuestion(true)}
            className="w-full p-4 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          >
            <span className="font-medium text-red-800">是的，我有这些问题</span>
          </button>
          
          <button
            onClick={() => handleFilterQuestion(false)}
            className="w-full p-4 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
          >
            <span className="font-medium text-green-800">没有，我没有这些问题</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderWordInstruction = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">三词记忆测试</h2>
        <p className="text-gray-600">请仔细记住以下三个词语</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="text-center">
          <p className="text-lg text-gray-700 mb-4">请记住这三个词：</p>
          <div className="flex justify-center space-x-4 mb-6">
            {wordRecallWords.map((word, index) => (
              <div key={index} className="bg-white rounded-lg px-6 py-3 shadow-sm border">
                <span className="text-2xl font-bold text-blue-600">{word}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 mb-4">
            我们会在完成其他测试后询问您还记得这些词语吗
          </p>
          <button
            onClick={handleWordInstructionNext}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            我记住了，继续
          </button>
        </div>
      </div>
    </div>
  );


  const renderWordRecall = () => {
    const allOptions = ['花', '门', '米饭', '书', '水', '车', '树', '灯', '猫'];
    
    const toggleWord = (word: string) => {
      if (selectedWords.includes(word)) {
        setSelectedWords(selectedWords.filter(w => w !== word));
      } else {
        setSelectedWords([...selectedWords, word]);
      }
    };
    
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">三词回忆测试</h2>
          <p className="text-gray-600">请选择您记得的词语</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <p className="text-lg font-medium text-gray-900 mb-4">
            请从下面的选项中选择刚才要求您记住的三个词：
          </p>
          
          <div className="grid grid-cols-3 gap-3 mb-6">
            {allOptions.map((word) => (
              <button
                key={word}
                onClick={() => toggleWord(word)}
                className={`p-3 rounded-lg border-2 transition-all ${
                  selectedWords.includes(word)
                    ? 'bg-blue-100 border-blue-500 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                {word}
              </button>
            ))}
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            已选择：{selectedWords.length}/3 个词语
          </p>
          
          <button
            onClick={() => handleWordRecall(selectedWords)}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            完成回忆测试
          </button>
        </div>
      </div>
    );
  };

  // 新的时间定向测试步骤
  const renderTimeYear = () => {
    const currentYear = new Date().getFullYear();
    const yearOptions = [
      currentYear.toString(),
      (currentYear - 1).toString(), 
      (currentYear + 1).toString(),
      (currentYear - 2).toString()
    ];

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">时间定向测试</h2>
          <p className="text-gray-600">第1题 / 4题</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">今天是哪一年？</h3>
          <div className="grid grid-cols-2 gap-4">
            {yearOptions.map((year) => (
              <button
                key={year}
                onClick={() => handleTimeYear(year)}
                className="p-4 text-lg font-semibold bg-white border-2 border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                {year}年
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTimeMonth = () => {
    const currentMonth = new Date().getMonth() + 1;
    const monthOptions = [
      currentMonth.toString(),
      currentMonth > 1 ? (currentMonth - 1).toString() : '12',
      currentMonth < 12 ? (currentMonth + 1).toString() : '1',
      currentMonth > 2 ? (currentMonth - 2).toString() : (currentMonth + 10).toString()
    ];

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">时间定向测试</h2>
          <p className="text-gray-600">第2题 / 4题</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">现在是几月？</h3>
          <div className="grid grid-cols-2 gap-4">
            {monthOptions.map((month) => (
              <button
                key={month}
                onClick={() => handleTimeMonth(month)}
                className="p-4 text-lg font-semibold bg-white border-2 border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                {month}月
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTimeDate = () => {
    const currentDate = new Date().getDate();
    const dateOptions = [
      currentDate.toString(),
      (currentDate > 1 ? currentDate - 1 : 30).toString(),
      (currentDate < 31 ? currentDate + 1 : 1).toString(),
      (currentDate > 2 ? currentDate - 2 : 29).toString()
    ];

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">时间定向测试</h2>
          <p className="text-gray-600">第3题 / 4题</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">今天是几号？</h3>
          <div className="grid grid-cols-2 gap-4">
            {dateOptions.map((date) => (
              <button
                key={date}
                onClick={() => handleTimeDate(date)}
                className="p-4 text-lg font-semibold bg-white border-2 border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                {date}日
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTimeWeekday = () => {
    const weekdays = ['一', '二', '三', '四', '五', '六', '日'];

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">时间定向测试</h2>
          <p className="text-gray-600">第4题 / 4题</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">今天是星期几？</h3>
          <div className="grid grid-cols-2 gap-3">
            {weekdays.map((day) => (
              <button
                key={day}
                onClick={() => handleTimeWeekday(day)}
                className="p-4 text-lg font-semibold bg-white border-2 border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                星期{day}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // 新的空间定向测试步骤  
  const renderSpaceCity = () => {
    // 测试环境下的预置城市选项
    const baseCityOptions = ['成都', '北京', '上海', '广州'];
    const cityOptions = suggestedCity
      ? Array.from(new Set([suggestedCity, ...baseCityOptions]))
      : baseCityOptions;
    
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">空间定向测试</h2>
          <p className="text-gray-600">第1题 / 4题</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">您现在在哪个城市？</h3>
          <div className="grid grid-cols-2 gap-4">
            {cityOptions.map((city) => (
              <button
                key={city}
                onClick={() => handleSpaceCity(city)}
                className="p-4 text-lg font-semibold bg-white border-2 border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors"
              >
                {city}
              </button>
            ))}
          </div>
          {/* 以上都不是选项 */}
          <button
            onClick={() => handleSpaceCity('其他城市')}
            className="w-full mt-4 p-3 text-base font-medium bg-gray-100 border-2 border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-200 transition-colors text-gray-700"
          >
            以上都不是
          </button>
          <p className="text-xs text-gray-500 mt-4 text-center">
            * 系统会根据您的位置智能推荐选项
          </p>
        </div>
      </div>
    );
  };

  const renderSpacePlace = () => {
    const placeOptions = ['在家里', '在医院', '在诊所', '在社区中心', '在其他地方'];

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">空间定向测试</h2>
          <p className="text-gray-600">第2题 / 4题</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">您现在在什么地方？</h3>
          <div className="space-y-3">
            {placeOptions.map((place) => (
              <button
                key={place}
                onClick={() => handleSpacePlace(place)}
                className="w-full p-4 text-lg font-semibold bg-white border-2 border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-left"
              >
                {place}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSpaceArea = () => {
    const areaOptions = ['客厅', '卧室', '书房', '诊室', '等候区', '其他房间'];

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">空间定向测试</h2>
          <p className="text-gray-600">第3题 / 4题</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">您在这个地方的哪个区域？</h3>
          <div className="grid grid-cols-2 gap-3">
            {areaOptions.map((area) => (
              <button
                key={area}
                onClick={() => handleSpaceArea(area)}
                className="p-4 text-lg font-semibold bg-white border-2 border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors"
              >
                {area}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSpaceTransport = () => {
    const transportOptions = ['步行来的', '开车来的', '家人送来的', '乘坐公交来的', '其他方式'];

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">空间定向测试</h2>
          <p className="text-gray-600">第4题 / 4题</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">您是怎么到这里来的？</h3>
          <div className="space-y-3">
            {transportOptions.map((transport) => (
              <button
                key={transport}
                onClick={() => handleSpaceTransport(transport)}
                className="w-full p-4 text-lg font-semibold bg-white border-2 border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-left"
              >
                {transport}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // 结果显示页面
  const renderResults = () => {
    const timeCorrectCount = Object.values(timeResults).filter(result => result === true).length;
    const timeInfo = getCurrentTimeInfo();
    const timePassed = timeCorrectCount >= 3;
    const spaceValidation = validateSpaceAnswers();
    const spacePassed = spaceValidation.passed;
    const wordsCorrect = (responses.wordRecall?.correctCount || 0) === 3;

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">认知测试结果</h2>
          <p className="text-gray-600">系统自动评估结果</p>
        </div>

        <div className="space-y-4">
          {/* 时间定向结果 */}
          <div className={`p-4 rounded-lg border ${timePassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h3 className="font-semibold text-gray-900 mb-2">时间定向测试 ({timeCorrectCount}/4)</h3>
            <div className="text-sm space-y-1">
              <p>年份：{userAnswers.year} {timeResults.year ? '✅' : '❌'} (正确：{timeInfo.year}年)</p>
              <p>月份：{userAnswers.month} {timeResults.month ? '✅' : '❌'} (正确：{timeInfo.month}月)</p>
              <p>日期：{userAnswers.date} {timeResults.date ? '✅' : '❌'} (正确：{timeInfo.date}日)</p>
              <p>星期：{userAnswers.weekday} {timeResults.weekday ? '✅' : '❌'} (正确：星期{timeInfo.weekdayName})</p>
            </div>
          </div>

          {/* 空间定向结果 */}
          <div className={`p-4 rounded-lg border ${spacePassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h3 className="font-semibold text-gray-900 mb-2">空间定向测试</h3>
            <div className="text-sm space-y-1">
              <p>城市：{userAnswers.city} {spaceValidation.details.city ? '✅' : '❌'}</p>
              <p>地点：{userAnswers.place} {spaceValidation.details.place ? '✅' : '❌'}</p>
              <p>区域：{userAnswers.area} {spaceValidation.details.area ? '✅' : '❌'}</p>
              <p>交通方式：{userAnswers.transport} {spaceValidation.details.transport ? '✅' : '❌'}</p>
              {spaceValidation.details.logicalError && (
                <p className="text-xs text-red-600 mt-2">
                  ❌ 逻辑冲突：{spaceValidation.details.errorReason}
                </p>
              )}
              {!spaceValidation.details.logicalError && spaceValidation.details.allBasicValid && (
                <p className="text-xs text-green-600 mt-2">✅ 逻辑合理</p>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">*系统检查明显的逻辑冲突</p>
          </div>

          {/* 词汇记忆结果 */}
          <div className={`p-4 rounded-lg border ${wordsCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h3 className="font-semibold text-gray-900 mb-2">词汇记忆测试 ({responses.wordRecall?.correctCount || 0}/3)</h3>
            <div className="text-sm space-y-1">
              <p>要求记忆：{wordRecallWords.join('、')}</p>
              <p>用户选择：{responses.wordRecall?.selectedWords?.join('、') || '无'}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {wordRecallWords.map(word => (
                  <span 
                    key={word} 
                    className={`px-2 py-1 rounded text-xs ${
                      responses.wordRecall?.selectedWords?.includes(word) 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {word} {responses.wordRecall?.selectedWords?.includes(word) ? '✅' : '❌'}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={handleResultsConfirm}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            确认结果，完成测试
          </button>
          
          <button
            onClick={() => setCurrentStep('carer_correction')}
            className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition-colors"
          >
            照护者修正答案
          </button>
        </div>
      </div>
    );
  };

  // 照护者修正页面
  const renderCarerCorrection = () => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">照护者答案确认</h2>
        <p className="text-gray-600">请照护者根据实际情况确认答案正确性</p>
      </div>

      <div className="space-y-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">时间定向答案确认</h3>
          <div className="text-sm text-gray-600 mb-4">
            用户回答：{userAnswers.year}年 {userAnswers.month}月 {userAnswers.date}日 星期{userAnswers.weekday}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setCarerCorrection(prev => ({ ...prev, timeCorrect: true }))}
              className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                carerCorrection.timeCorrect === true 
                  ? 'bg-green-100 border-green-500 text-green-700' 
                  : 'bg-white border-gray-300 hover:border-green-400'
              }`}
            >
              ✅ 时间回答正确
            </button>
            <button
              onClick={() => setCarerCorrection(prev => ({ ...prev, timeCorrect: false }))}
              className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                carerCorrection.timeCorrect === false 
                  ? 'bg-red-100 border-red-500 text-red-700' 
                  : 'bg-white border-gray-300 hover:border-red-400'
              }`}
            >
              ❌ 时间回答错误
            </button>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">空间定向答案确认</h3>
          <div className="text-sm text-gray-600 mb-4">
            用户回答：{userAnswers.city}，{userAnswers.place}，{userAnswers.area}，{userAnswers.transport}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setCarerCorrection(prev => ({ ...prev, spaceCorrect: true }))}
              className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                carerCorrection.spaceCorrect === true 
                  ? 'bg-green-100 border-green-500 text-green-700' 
                  : 'bg-white border-gray-300 hover:border-green-400'
              }`}
            >
              ✅ 空间回答正确
            </button>
            <button
              onClick={() => setCarerCorrection(prev => ({ ...prev, spaceCorrect: false }))}
              className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                carerCorrection.spaceCorrect === false 
                  ? 'bg-red-100 border-red-500 text-red-700' 
                  : 'bg-white border-gray-300 hover:border-red-400'
              }`}
            >
              ❌ 空间回答错误
            </button>
          </div>
        </div>

        <button
          onClick={() => {
            if (carerCorrection.timeCorrect !== null && carerCorrection.spaceCorrect !== null) {
              handleCarerCorrection(carerCorrection.timeCorrect, carerCorrection.spaceCorrect);
            }
          }}
          disabled={carerCorrection.timeCorrect === null || carerCorrection.spaceCorrect === null}
          className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          确认修正，完成评估
        </button>
      </div>
    </div>
  );

  const renderCompleted = () => (
    <div className="text-center space-y-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">认知能力评估完成</h2>
      <p className="text-gray-600">正在进入下一项评估...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {currentStep === 'filter' && renderFilterQuestion()}
        {currentStep === 'word_instruction' && renderWordInstruction()}
        {currentStep === 'time_year' && renderTimeYear()}
        {currentStep === 'time_month' && renderTimeMonth()}
        {currentStep === 'time_date' && renderTimeDate()}
        {currentStep === 'time_weekday' && renderTimeWeekday()}
        {currentStep === 'space_city' && renderSpaceCity()}
        {currentStep === 'space_place' && renderSpacePlace()}
        {currentStep === 'space_area' && renderSpaceArea()}
        {currentStep === 'space_transport' && renderSpaceTransport()}
        {currentStep === 'word_recall' && renderWordRecall()}
        {currentStep === 'results' && renderResults()}
        {currentStep === 'carer_correction' && renderCarerCorrection()}
        {currentStep === 'completed' && renderCompleted()}
      </div>
    </div>
  );
};

export default CognitionAssessment;
